"use client";

import HeroSection from "@/components/page-section/HeroSection/HeroSection";
import { EditorViewer } from "@/components/shared/EditorViewer";
import type { PageContent } from "@/services/api/page-contents";
import {
  overlayDefaultsFromCmsFields,
  slideItemsFromCmsRows,
  type CmsHeroSlideRow,
} from "@/utils/cms-hero-slides";
import { normalizeCmsAssetUrl } from "@/utils/media-url";

interface DaiHocHeroProps {
  content?: PageContent | null;
}

type DaiHocHeroContent = {
  type?: string;
  content?: unknown;
  image?: string;
  slides?: CmsHeroSlideRow[];
  brand?: string;
  title?: string;
  button?: {
    label?: string;
    href?: string;
    variant?: string;
    openInNewTab?: boolean;
    target?: string;
  };
};

const isDaiHocHero = (value: unknown): value is DaiHocHeroContent =>
  !!value &&
  typeof value === "object" &&
  (value as DaiHocHeroContent).type === "dai-hoc-hero-section";

const FALLBACK = {
  brand: "HUB",
  title: "ĐẠI HỌC CHÍNH QUY",
  label: "Xem chi tiết",
  href: "#about",
};

function parseContent(raw: unknown): DaiHocHeroContent | null {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      return isDaiHocHero(p) ? p : null;
    } catch {
      return {
        type: "dai-hoc-hero-section",
        content: raw,
        image: "/assets/images/hero_bg.png",
      };
    }
  }
  return isDaiHocHero(raw) ? raw : null;
}

export default function DaiHocHero({ content }: DaiHocHeroProps) {
  const resolved = parseContent(content?.content);

  const heroSlides = (() => {
    const fromSlides = slideItemsFromCmsRows(resolved?.slides ?? null);
    if (fromSlides) return fromSlides;
    if (resolved?.image) {
      const img = normalizeCmsAssetUrl(resolved.image) || resolved.image;
      return [{ image: img, alt: "Banner Đại Học" }];
    }
    return null;
  })();

  /** Chỉ dùng overlay giống Home khi CMS có mảng `slides` (kể cả 1 ảnh). Legacy chỉ image+content giữ EditorViewer. */
  const hasNewCmsShape = !!resolved?.slides?.length;

  const overlayDefaults = hasNewCmsShape
    ? overlayDefaultsFromCmsFields(
        resolved?.brand,
        resolved?.title,
        resolved?.button,
        FALLBACK,
      )
    : null;

  return (
    <HeroSection
      pageKey="dai-hoc"
      variant="carousel"
      sectionModifier="hero-section--banner hero-section--daihoc"
      ariaLabel="Banner Đại Học Chính Quy"
      breadcrumb={[
        { label: "Trang chủ", href: "/" },
        { label: "Đại học" },
      ]}
      overrideSlides={heroSlides}
      overlayDefaults={overlayDefaults}
    >
      {!overlayDefaults ? (
        <div className="hero-section__overlay">
          <div className="container h-100">
            <div className="hero-section__content">
              <EditorViewer
                editorSerializedState={resolved?.content}
                as="h1"
                id="hero-title"
                className="hero-section__title"
                fallback={
                  <h1 id="hero-title" className="hero-section__title">
                    ĐẠI HỌC CHÍNH QUY
                  </h1>
                }
              />
            </div>
          </div>
        </div>
      ) : null}
    </HeroSection>
  );
}
